import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronLeft, MessageCircle, Send, Mic, Building2, Phone, RectangleHorizontal, Car, ShieldCheck, Zap, Bot } from 'lucide-react';
import { Button } from '../ui/button';
import { HERO_IMAGE, LOGO_DARK, LOGO_LIGHT } from '../../constants/images';

const PROMPT_CHIPS = {
  ar: [
    { icon: Building2, label: 'تأسيس شركة في راكز', prompt: 'كم تكلفة تأسيس شركة في المنطقة الحرة برأس الخيمة؟' },
    { icon: Phone, label: 'رقم مميز 050', prompt: 'أبحث عن رقم مميز يبدأ بـ 050 بميزانية مناسبة' },
    { icon: RectangleHorizontal, label: 'لوحة دبي', prompt: 'ما هي لوحات دبي المتاحة حالياً؟' },
    { icon: Car, label: 'سيارة مناسبة', prompt: 'ساعدني في اختيار سيارة عائلية بميزانية 150 ألف درهم' },
  ],
  en: [
    { icon: Building2, label: 'Company in RAKEZ', prompt: 'How much does it cost to set up a company in RAKEZ free zone?' },
    { icon: Phone, label: 'Premium 050 number', prompt: 'I am looking for a premium number starting with 050' },
    { icon: RectangleHorizontal, label: 'Dubai plate', prompt: 'Which Dubai plates are available right now?' },
    { icon: Car, label: 'Pick a car', prompt: 'Help me choose a family car with a 150k AED budget' },
  ],
};

export const HeroSection = ({ isDarkMode, isAr, openChat }) => {
  const [prompt, setPrompt] = useState('');
  const chips = PROMPT_CHIPS[isAr ? 'ar' : 'en'];

  const submitPrompt = (e) => {
    e?.preventDefault?.();
    openChat(prompt.trim() || undefined);
    setPrompt('');
  };

  const glass = isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light';
  const textMain = isDarkMode ? 'text-white' : 'text-[#0F172A]';
  const textMuted = isDarkMode ? 'text-slate-400' : 'text-slate-600';

  return (
    <section className="relative min-h-[88vh] flex items-center overflow-hidden z-10 animate-fadeIn" data-testid="hero-section">
      {/* Background: skyline + brand wash + AI dot grid */}
      <div className="absolute inset-0">
        <img src={HERO_IMAGE} alt="" className={`w-full h-full object-cover ${isDarkMode ? 'opacity-[0.16]' : 'opacity-[0.10]'}`} />
        <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-b from-[#0A0B10] via-[#0A0B10]/85 to-[#0A0B10]' : 'bg-gradient-to-b from-white via-white/85 to-[#F8FAFC]'}`} />
        <div className={`absolute inset-0 ${isDarkMode ? 'cgm-dot-grid opacity-40' : 'cgm-dot-grid-light opacity-50'} [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]`} />
      </div>

      {/* Ambient orbs — logo blue + gold */}
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute -top-20 start-[10%] w-[520px] h-[520px] rounded-full blur-[130px] ${isDarkMode ? 'bg-[#1E9BD9]/20' : 'bg-[#1E9BD9]/15'}`} />
        <div className={`absolute bottom-0 end-[8%] w-[420px] h-[420px] rounded-full blur-[120px] ${isDarkMode ? 'bg-[#F5A962]/15' : 'bg-[#F5A962]/20'}`} />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-10 lg:py-14 w-full">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">

          {/* ───── Text + AI prompt bar (7 cols) ───── */}
          <div className="lg:col-span-7 space-y-7 text-center lg:text-start order-last lg:order-first">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${isDarkMode ? 'bg-[#1E9BD9]/10 border-[#1E9BD9]/30' : 'bg-white border-[#1E9BD9]/30 shadow-sm'}`} data-testid="hero-ai-badge">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <Sparkles className="h-4 w-4 text-[#1E9BD9]" />
              <span className={`text-xs sm:text-sm font-semibold ${isDarkMode ? 'cgm-brand-text' : 'text-[#0284C7]'}`}>
                {isAr ? 'منصة مدعومة بالذكاء الاصطناعي · الإمارات' : 'AI-powered platform · UAE'}
              </span>
            </div>

            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight ${textMain}`}>
              {isAr ? (
                <>
                  كل خدماتك وسوقك
                  <span className="block mt-2 pb-2 cgm-brand-text">بذكاء واحد</span>
                </>
              ) : (
                <>
                  Your services & market
                  <span className="block mt-2 pb-2 cgm-brand-text">powered by one AI</span>
                </>
              )}
            </h1>

            <p className={`text-base sm:text-lg max-w-xl mx-auto lg:mx-0 leading-relaxed ${textMuted}`}>
              {isAr
                ? 'تأسيس الشركات، الترجمة القانونية، الأرقام المميزة، لوحات السيارات، السيارات والعقارات — اسأل مساعد CGM AI وسيدلّك مباشرة.'
                : 'Company formation, legal translation, premium numbers, plates, cars and real estate — ask CGM AI and it guides you instantly.'}
            </p>

            {/* AI prompt bar */}
            <form onSubmit={submitPrompt} className={`relative rounded-2xl p-1.5 ${glass} shadow-[0_10px_40px_rgba(30,155,217,0.15)]`} data-testid="hero-ai-prompt-bar">
              <div className="absolute inset-x-6 top-0 cgm-scanline rounded-full" />
              <div className="flex items-center gap-2">
                <span className={`hidden sm:flex w-10 h-10 rounded-xl items-center justify-center shrink-0 ${isDarkMode ? 'bg-[#1E9BD9]/15' : 'bg-[#1E9BD9]/10'}`}>
                  <Bot className="h-5 w-5 text-[#1E9BD9]" />
                </span>
                <input
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={isAr ? 'اسأل CGM AI… مثلاً: كم تكلفة تأسيس شركة في راكز؟' : 'Ask CGM AI… e.g. How much is a RAKEZ company?'}
                  className={`flex-1 min-w-0 h-11 bg-transparent outline-none text-sm sm:text-base px-2 ${textMain} placeholder:text-slate-400`}
                  data-testid="hero-ai-prompt-input"
                />
                <button
                  type="button"
                  onClick={() => openChat()}
                  className={`hidden sm:flex w-10 h-10 rounded-xl items-center justify-center shrink-0 transition-colors ${isDarkMode ? 'text-slate-400 hover:text-[#1E9BD9] hover:bg-white/5' : 'text-slate-500 hover:text-[#1E9BD9] hover:bg-slate-100'}`}
                  title={isAr ? 'تحدث بالصوت' : 'Talk with voice'}
                  data-testid="hero-ai-voice-btn"
                >
                  <Mic className="h-5 w-5" />
                </button>
                <button
                  type="submit"
                  className="h-11 px-4 sm:px-5 rounded-xl text-white text-sm font-bold inline-flex items-center gap-2 bg-gradient-to-r from-[#1E9BD9] via-[#38BDF8] to-[#F5A962] hover:shadow-[0_8px_24px_rgba(30,155,217,0.45)] hover:-translate-y-0.5 transition-all"
                  data-testid="hero-ai-prompt-submit"
                >
                  <Send className={`h-4 w-4 ${isAr ? 'rotate-180' : ''}`} />
                  <span className="hidden sm:inline">{isAr ? 'اسأل' : 'Ask'}</span>
                </button>
              </div>
            </form>

            <div className="flex flex-wrap gap-2 justify-center lg:justify-start" data-testid="hero-prompt-chips">
              {chips.map((c, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => openChat(c.prompt)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all hover:-translate-y-0.5 ${isDarkMode ? 'bg-white/[0.03] border-white/10 text-slate-300 hover:border-[#1E9BD9]/50 hover:text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-[#1E9BD9]/50 hover:text-[#0F172A] shadow-sm'}`}
                  data-testid={`hero-chip-${i}`}
                >
                  <c.icon className="h-3.5 w-3.5 text-[#1E9BD9]" />
                  {c.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 justify-center lg:justify-start pt-1">
              <Link to="/services">
                <Button className="bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:from-[#0284C7] hover:to-[#1E9BD9] text-white px-6 py-5 rounded-xl text-base font-semibold shadow-lg shadow-[#1E9BD9]/25 transition-all hover:-translate-y-0.5" data-testid="hero-explore-services">
                  {isAr ? 'استكشف الخدمات' : 'Explore Services'}
                  <ChevronLeft className={`h-5 w-5 ms-2 ${!isAr && 'rotate-180'}`} />
                </Button>
              </Link>
              <Link to="/mobile-numbers">
                <Button variant="outline" className={`border px-6 py-5 rounded-xl text-base font-semibold transition-all hover:-translate-y-0.5 ${isDarkMode ? 'border-[#F5A962]/40 text-white hover:bg-[#F5A962]/10' : 'border-[#F5A962]/50 text-slate-800 hover:bg-[#F5A962]/10'}`} data-testid="hero-browse-market">
                  {isAr ? 'تصفح السوق' : 'Browse Marketplace'}
                </Button>
              </Link>
            </div>

            {/* Trust strip */}
            <div className={`grid grid-cols-3 gap-3 pt-5 border-t max-w-lg mx-auto lg:mx-0 ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`} data-testid="hero-stats">
              {[
                { v: '500+', l: isAr ? 'عميل سعيد' : 'Happy clients', icon: ShieldCheck },
                { v: '50+', l: isAr ? 'شركة تأسست' : 'Companies formed', icon: Building2 },
                { v: '24/7', l: isAr ? 'مساعد ذكي' : 'AI assistant', icon: Zap },
              ].map((s, i) => (
                <div key={i} className="text-center lg:text-start">
                  <div className={`text-xl sm:text-2xl font-black tabular-nums ${textMain}`}>{s.v}</div>
                  <div className={`text-[11px] sm:text-xs inline-flex items-center gap-1 ${textMuted}`}><s.icon className="h-3 w-3 text-[#1E9BD9]" />{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ───── AI Orb visual (5 cols) ───── */}
          <div className="lg:col-span-5 relative flex items-center justify-center mt-14 mb-4 lg:my-0 min-h-[300px] sm:min-h-[360px]">
            <div className="absolute w-[300px] h-[300px] sm:w-[360px] sm:h-[360px] lg:w-[400px] lg:h-[400px] rounded-full border border-dashed border-[#1E9BD9]/30 cgm-orbit">
              <span className="absolute -top-1.5 left-1/2 w-3 h-3 rounded-full bg-[#1E9BD9] shadow-[0_0_14px_#1E9BD9]" />
            </div>
            <div className="absolute w-[240px] h-[240px] sm:w-[290px] sm:h-[290px] lg:w-[320px] lg:h-[320px] rounded-full border border-[#F5A962]/30 cgm-orbit-reverse">
              <span className="absolute top-1/2 -right-1.5 w-2.5 h-2.5 rounded-full bg-[#F5A962] shadow-[0_0_14px_#F5A962]" />
            </div>
            <div className={`absolute w-[260px] h-[260px] sm:w-[300px] sm:h-[300px] rounded-full blur-2xl cgm-breathe ${isDarkMode ? 'bg-gradient-to-br from-[#1E9BD9]/30 via-[#38BDF8]/10 to-[#F5A962]/25' : 'bg-gradient-to-br from-[#1E9BD9]/25 via-[#38BDF8]/10 to-[#F5A962]/25'}`} />

            <button onClick={() => openChat()} className="relative group focus:outline-none" data-testid="hero-ai-chat-trigger">
              <div className={`relative w-44 h-44 sm:w-52 sm:h-52 lg:w-60 lg:h-60 rounded-full flex flex-col items-center justify-center gap-2 overflow-hidden transition-all duration-500 group-hover:scale-[1.04] ${glass} ${isDarkMode ? 'shadow-[0_0_60px_rgba(30,155,217,0.25)]' : 'shadow-[0_20px_60px_rgba(30,155,217,0.2)]'}`}>
                <div className="absolute inset-x-6 h-10 bg-gradient-to-b from-transparent via-[#1E9BD9]/15 to-transparent cgm-scan-beam pointer-events-none" />
                <img src={isDarkMode ? LOGO_DARK : LOGO_LIGHT} alt="CloudGM" className="w-20 h-20 sm:w-24 sm:h-24 object-contain transition-transform duration-500 group-hover:scale-110" />
                <div className="flex items-center gap-1.5">
                  <MessageCircle className="w-4 h-4 text-[#1E9BD9]" />
                  <span className={`text-xs sm:text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'} group-hover:text-[#1E9BD9] transition-colors`}>
                    {isAr ? 'تحدث مع CGM AI' : 'Chat with CGM AI'}
                  </span>
                </div>
              </div>
              <div className="absolute inset-0 rounded-full border border-[#1E9BD9]/0 group-hover:border-[#1E9BD9]/50 group-hover:scale-125 transition-all duration-700 pointer-events-none" />
            </button>

            {/* Floating capability pills */}
            <div className={`absolute -top-2 start-2 sm:start-6 px-3 py-1.5 rounded-xl text-[11px] font-semibold ${glass} animate-float`} style={{ animationDelay: '0s' }}>
              <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-[#1E9BD9]" />{isAr ? 'تأسيس شركات' : 'Company setup'}</span>
            </div>
            <div className={`absolute bottom-3 end-2 sm:end-6 px-3 py-1.5 rounded-xl text-[11px] font-semibold ${glass} animate-float`} style={{ animationDelay: '0.8s' }}>
              <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-[#F5A962]" />{isAr ? 'أرقام مميزة' : 'Premium numbers'}</span>
            </div>
            <div className={`absolute top-1/2 -end-1 sm:end-0 px-3 py-1.5 rounded-xl text-[11px] font-semibold ${glass} animate-float hidden sm:block`} style={{ animationDelay: '1.6s' }}>
              <span className="inline-flex items-center gap-1.5"><Mic className="h-3.5 w-3.5 text-emerald-500" />{isAr ? 'رسائل صوتية' : 'Voice messages'}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
